import { inject, Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, concatMap, map, Observable, of, shareReplay, switchMap, take, tap, throwError } from 'rxjs';
import { environment } from '../../environments/environment';
import { AbschnittZustand, DashboardLayout, leeresLayout } from '../models/dashboard-layout.model';

type ProjektListe = 'angehefteteProjekte' | 'ausgeblendeteProjekte';

@Injectable({ providedIn: 'root' })
export class DashboardLayoutService {
  private http = inject(HttpClient);
  private apiUrl = `${environment.apiBaseUrl}/api/v1/users/me/dashboard-layout`;

  private readonly _layout = signal<DashboardLayout | null>(null);
  readonly layout = this._layout.asReadonly();

  private geladen$?: Observable<DashboardLayout | null>;
  private warteschlange: Observable<unknown> = of(undefined);

  laden(): Observable<DashboardLayout | null> {
    this.geladen$ ??= this.http.get<DashboardLayout>(this.apiUrl).pipe(
      map((layout) => ({ ...leeresLayout(), ...layout })),
      catchError(() => of(null)),
      tap((layout) => this._layout.set(layout)),
      shareReplay(1),
    );
    return this.geladen$;
  }

  abschnittZustand(key: string): AbschnittZustand | undefined {
    return this._layout()?.abschnitte[key];
  }

  /**
   * PUT ersetzt das ganze Layout. Ohne geladenes Layout wird nichts gesendet, sonst würden gespeicherte Pins mit
   * Standardwerten überschrieben; Änderungen laufen nacheinander, damit die zuletzt gewählte gewinnt.
   */
  setzeAbschnitt(key: string, wert: AbschnittZustand): Observable<void> {
    return this.einreihen(() => {
      const aktuell = this._layout()!;
      this._layout.set({ ...aktuell, abschnitte: { ...aktuell.abschnitte, [key]: wert } });
      return this.senden();
    });
  }

  toggleAngeheftet(projectId: string): Observable<void> {
    return this.umschalten('angehefteteProjekte', projectId);
  }

  toggleAusgeblendet(projectId: string): Observable<void> {
    return this.umschalten('ausgeblendeteProjekte', projectId);
  }

  /**
   * Schaltet sofort um, damit die Karte nicht auf den PUT wartet. Bei einem Fehler wird nur diese ID auf ihren
   * vorigen Stand gesetzt, damit inzwischen gemachte Klicks erhalten bleiben.
   */
  private umschalten(liste: ProjektListe, projectId: string): Observable<void> {
    let vorher: boolean | null = null;
    const setzen = (drin: boolean) =>
      this._layout.update((layout) => layout && { ...layout, [liste]: mitId(layout[liste], projectId, drin) });
    const umschaltenWennGeladen = () => {
      const layout = this._layout();
      if (!layout || vorher !== null) return;
      vorher = layout[liste].includes(projectId);
      setzen(!vorher);
    };

    umschaltenWennGeladen();
    return this.einreihen(() => {
      umschaltenWennGeladen();
      return this.senden().pipe(
        catchError((fehler) => {
          setzen(vorher!);
          return throwError(() => fehler);
        }),
      );
    });
  }

  private einreihen(aendern: () => Observable<void>): Observable<void> {
    const auftrag = this.warteschlange.pipe(
      catchError(() => of(undefined)),
      take(1),
      concatMap(() => this.laden().pipe(take(1))),
      switchMap((layout) => (layout ? aendern() : throwError(() => new Error('Layout nicht geladen')))),
      shareReplay(1),
    );
    this.warteschlange = auftrag;
    auftrag.subscribe({ error: () => undefined });
    return auftrag;
  }

  private senden(): Observable<void> {
    return this.http.put(this.apiUrl, this._layout()).pipe(map(() => undefined));
  }
}

function mitId(ids: string[], id: string, drin: boolean): string[] {
  const ohne = ids.filter((vorhanden) => vorhanden !== id);
  return drin ? [...ohne, id] : ohne;
}
